import { useMemo, useRef, useState } from "react";
import { Link } from "react-router";

import {
  useGetAdminV2EventTaxonomyQuery,
  useGetAdminV2IntegrationsQuery,
  useGetAdminV2MeQuery,
  useGetAdminV2OpsWhatNeedsAttentionQuery,
  useGetAdminV2PosthogDashboardsQuery,
  usePatchAdminV2EventTaxonomyMutation,
  usePatchAdminV2IntegrationConnectionMutation,
} from "@/store/api/baseApi";
import { getErrorMessage, getPublicErrorDetails } from "@/utils/errorHandling";
import {
  VNextButton,
  VNextCard,
  VNextDialog,
  VNextField,
  VNextInput,
  VNextKeyValueList,
  VNextNotice,
  VNextPill,
  VNextSelect,
  VNextStat,
  VNextTextArea,
} from "@/vnext/primitives";
import { InsightsWorkspaceChrome } from "@/vnext/routes/insights/InsightsWorkspaceChrome";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";
import {
  formatVNextDateTime,
  formatVNextLabel,
  formatVNextNumber,
} from "@/vnext/shared/formatters";
import { useUrlTab } from "@/vnext/shared/useUrlTab";

import "@/vnext/routes/insights/insightsMake.css";

type InsightsTab = "dashboard" | "dashboards" | "taxonomy" | "integrations";
type InsightsMode = "workspace" | "settings";

/**
 * Address values match the navigation map's tab ids (adminNavModel).
 * Integrations belongs to Settings (Kate §11:158): the Insights workspace
 * neither lists it nor opens it from an old address.
 */
const INSIGHTS_TAB_IDS = {
  dashboard: "dashboard",
  posthog: "dashboards",
  taxonomy: "taxonomy",
  integrations: "integrations",
} as const satisfies Record<string, InsightsTab>;

const TABS: Array<{ label: string; value: InsightsTab }> = [
  { label: "Dashboard", value: "dashboard" },
  { label: "PostHog dashboards", value: "dashboards" },
  { label: "Event taxonomy", value: "taxonomy" },
  { label: "Integrations", value: "integrations" },
];

const isCanonicalPublicErrorPayload = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null || !("data" in error)) {
    return false;
  }

  const data = (error as { data?: unknown }).data;
  return (
    typeof data === "object" &&
    data !== null &&
    "errorCode" in data &&
    "message" in data &&
    "correlationId" in data
  );
};

// fe37: a served reply without its list (a stale mock or contract drift)
// counts as Unavailable instead of throwing and taking the page down.
const listCount = (list: readonly unknown[] | undefined): number | undefined =>
  Array.isArray(list) ? list.length : undefined;

const formatQueryCount = (
  count: number | undefined,
  queryState: {
    isError: boolean;
    isLoading: boolean;
  },
): string => {
  if (queryState.isError || (count === undefined && !queryState.isLoading)) {
    return "Unavailable";
  }

  return count === undefined ? "Loading" : formatVNextNumber(count);
};

interface InsightsRouteProps {
  initialTab?: InsightsTab;
  mode?: InsightsMode;
}

export default function InsightsRoute({
  initialTab = "dashboard",
  mode = "workspace",
}: InsightsRouteProps) {
  const {
    tab: activeTab,
    setTab: setActiveTab,
    getHref: getTabHref,
  } = useUrlTab<InsightsTab>(INSIGHTS_TAB_IDS, initialTab);
  const currentTab: InsightsTab =
    mode === "settings"
      ? "integrations"
      : activeTab === "integrations"
        ? "dashboard"
        : activeTab;
  const [feedback, setFeedback] = useState<string | null>(null);
  const tabRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const integrationStatusChangeInFlightRef = useRef(false);
  const [pendingIntegrationConfirmation, setPendingIntegrationConfirmation] =
    useState<{
      category: string;
      connectionId: string;
      currentStatus: "enabled" | "disabled";
      displayName: string;
      healthStatus: string;
      nextStatus: "enabled" | "disabled";
    } | null>(null);
  const [
    isConfirmingIntegrationStatusChange,
    setIsConfirmingIntegrationStatusChange,
  ] = useState(false);
  const [taxonomyEventName, setTaxonomyEventName] = useState("");
  const [taxonomyStatus, setTaxonomyStatus] = useState("active");
  const [taxonomyAnnotation, setTaxonomyAnnotation] = useState("");
  const [taxonomyDeprecatedReason, setTaxonomyDeprecatedReason] = useState("");
  const [taxonomyReplacement, setTaxonomyReplacement] = useState("");

  const meQuery = useGetAdminV2MeQuery();
  const permissionKeys = useMemo(
    () => new Set(meQuery.data?.permissionKeys ?? []),
    [meQuery.data?.permissionKeys],
  );
  const canViewAnalytics = permissionKeys.has("analytics.view");
  const canViewIntegrations = permissionKeys.has("integrations.view");
  const canManageIntegrations = permissionKeys.has("integrations.manage");
  const opsQuery = useGetAdminV2OpsWhatNeedsAttentionQuery(undefined, {
    skip: mode === "settings" || !canViewAnalytics,
  });
  const dashboardsQuery = useGetAdminV2PosthogDashboardsQuery(undefined, {
    skip: mode === "settings" || !canViewAnalytics,
  });
  const taxonomyQuery = useGetAdminV2EventTaxonomyQuery(undefined, {
    skip: mode === "settings" || !canViewAnalytics,
  });
  const integrationsQuery = useGetAdminV2IntegrationsQuery(undefined, {
    skip: !canViewIntegrations,
  });
  const [patchTaxonomy, patchTaxonomyState] =
    usePatchAdminV2EventTaxonomyMutation();
  const [patchConnection, patchConnectionState] =
    usePatchAdminV2IntegrationConnectionMutation();

  const alerts = opsQuery.data?.items ?? [];
  const dashboards = dashboardsQuery.data?.dashboards ?? [];
  const taxonomyItems = taxonomyQuery.data?.items ?? [];
  const integrationItems = integrationsQuery.data?.items ?? [];
  const dashboardsErrorDetails = dashboardsQuery.error
    ? getPublicErrorDetails(
        dashboardsQuery.error,
        "Unable to load PostHog dashboards right now.",
      )
    : null;
  const dashboardsQueryUsesCanonicalPublicError = dashboardsQuery.error
    ? isCanonicalPublicErrorPayload(dashboardsQuery.error)
    : false;
  const dashboardsQueryErrorCode = dashboardsQueryUsesCanonicalPublicError
    ? (dashboardsErrorDetails?.errorCode ?? null)
    : null;
  const routeTitle = mode === "settings" ? "Integrations" : "Insights";
  const routeSubtitle =
    mode === "settings"
      ? "Integration health and connection operations."
      : "Product intelligence, taxonomy governance, and integration health.";
  const workspaceHealthSubtitle =
    mode === "settings"
      ? "Start here to confirm connection health before you enable or disable an integration."
      : "Start here for a health overview before opening detailed dashboards or the event taxonomy.";
  const visibleTabs =
    mode === "settings"
      ? TABS.filter((tab) => tab.value === "integrations")
      : TABS.filter((tab) => tab.value !== "integrations");
  const freshnessLabel = dashboards[0]?.updatedAt
    ? formatVNextDateTime(dashboards[0].updatedAt)
    : "Live data";
  const WorkspaceChrome =
    mode === "settings" ? SettingsWorkspaceChrome : InsightsWorkspaceChrome;

  const handlePatchTaxonomy = async () => {
    if (!taxonomyEventName.trim()) {
      setFeedback("Event name is required to patch taxonomy.");
      return;
    }

    setFeedback(null);
    try {
      await patchTaxonomy({
        body: {
          annotation: taxonomyAnnotation.trim() || null,
          deprecatedReason: taxonomyDeprecatedReason.trim() || null,
          replacementEventName: taxonomyReplacement.trim() || null,
          status: taxonomyStatus as "active" | "deprecated",
        },
        eventName: taxonomyEventName.trim(),
      }).unwrap();
      setFeedback("Taxonomy event updated.");
    } catch (error) {
      setFeedback(getErrorMessage(error));
    }
  };

  const handlePatchIntegration = async (
    connectionId: string,
    nextStatus: "enabled" | "disabled",
  ) => {
    if (integrationStatusChangeInFlightRef.current) {
      return;
    }

    integrationStatusChangeInFlightRef.current = true;
    setIsConfirmingIntegrationStatusChange(true);
    setFeedback(null);
    try {
      await patchConnection({
        body: { status: nextStatus },
        connectionId,
      }).unwrap();
      setPendingIntegrationConfirmation(null);
    } catch (error) {
      const details = getPublicErrorDetails(
        error,
        "Unable to update integration right now.",
      );
      setFeedback(
        `Unable to update integration right now. Error code: ${details.errorCode}`,
      );
      setPendingIntegrationConfirmation(null);
    } finally {
      integrationStatusChangeInFlightRef.current = false;
      setIsConfirmingIntegrationStatusChange(false);
    }
  };

  const closeIntegrationConfirmation = () => {
    if (
      integrationStatusChangeInFlightRef.current ||
      !pendingIntegrationConfirmation
    ) {
      return;
    }

    setFeedback(
      `Cancelled ${
        pendingIntegrationConfirmation.nextStatus === "enabled"
          ? "enabling"
          : "disabling"
      } ${pendingIntegrationConfirmation.displayName}.`,
    );
    setPendingIntegrationConfirmation(null);
  };

  const requestIntegrationStatusChange = ({
    category,
    connectionId,
    currentStatus,
    displayName,
    healthStatus,
    nextStatus,
  }: {
    category: string;
    connectionId: string;
    currentStatus: "enabled" | "disabled";
    displayName: string;
    healthStatus: string;
    nextStatus: "enabled" | "disabled";
  }) => {
    setPendingIntegrationConfirmation({
      category,
      connectionId,
      currentStatus,
      displayName,
      healthStatus,
      nextStatus,
    });
    setFeedback(null);
  };

  const selectTaxonomyItem = (item: (typeof taxonomyItems)[number]) => {
    setTaxonomyEventName(item.eventName);
    setTaxonomyStatus(item.status);
    setTaxonomyAnnotation(item.annotation ?? "");
    setTaxonomyDeprecatedReason(item.deprecatedReason ?? "");
    setTaxonomyReplacement(item.replacementEventName ?? "");
  };

  return (
    <WorkspaceChrome
      actions={
        <div className="vnext-chip-list">
          <VNextPill>{freshnessLabel}</VNextPill>
        </div>
      }
      subtitle={routeSubtitle}
      title={routeTitle}
    >
      <div className="insights-make-page">
        {visibleTabs.length > 1 ? (
          <div
            aria-label="Insights views"
            className="insights-make-tabs"
            role="tablist"
          >
            {visibleTabs.map((tab, index) => (
              <Link
                aria-selected={currentTab === tab.value}
                className={`insights-make-tabs__item${
                  currentTab === tab.value ? " is-active" : ""
                }`}
                key={tab.value}
                onClick={(event) => {
                  if (
                    event.button !== 0 ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey
                  )
                    return;
                  event.preventDefault();
                  setActiveTab(tab.value);
                }}
                role="tab"
                tabIndex={currentTab === tab.value ? 0 : -1}
                ref={(element) => {
                  tabRefs.current[index] = element;
                }}
                to={getTabHref(tab.value)}
                onKeyDown={(event) => {
                  if (event.key === " ") {
                    event.preventDefault();
                    setActiveTab(tab.value);
                    return;
                  }
                  const last = visibleTabs.length - 1;
                  const next =
                    event.key === "ArrowRight"
                      ? (index + 1) % visibleTabs.length
                      : event.key === "ArrowLeft"
                        ? (index + last) % visibleTabs.length
                        : event.key === "Home"
                          ? 0
                          : event.key === "End"
                            ? last
                            : null;
                  if (next === null) return;
                  event.preventDefault();
                  setActiveTab(visibleTabs[next].value);
                  tabRefs.current[next]?.focus();
                }}
              >
                {tab.label}
              </Link>
            ))}
          </div>
        ) : null}

        {pendingIntegrationConfirmation ? (
          <VNextDialog
            description="Review before changing this integration connection."
            footer={
              <>
                <VNextButton
                  disabled={isConfirmingIntegrationStatusChange}
                  onClick={closeIntegrationConfirmation}
                  tone="secondary"
                >
                  Cancel
                </VNextButton>
                <VNextButton
                  disabled={
                    patchConnectionState.isLoading ||
                    isConfirmingIntegrationStatusChange
                  }
                  onClick={() =>
                    void handlePatchIntegration(
                      pendingIntegrationConfirmation.connectionId,
                      pendingIntegrationConfirmation.nextStatus,
                    )
                  }
                  tone="primary"
                >
                  {isConfirmingIntegrationStatusChange
                    ? pendingIntegrationConfirmation.nextStatus === "enabled"
                      ? "Enabling..."
                      : "Disabling..."
                    : pendingIntegrationConfirmation.nextStatus === "enabled"
                      ? "Confirm enable"
                      : "Confirm disable"}
                </VNextButton>
              </>
            }
            onClose={closeIntegrationConfirmation}
            open
            title={
              pendingIntegrationConfirmation.nextStatus === "enabled"
                ? "Enable integration"
                : "Disable integration"
            }
          >
            <div className="vnext-stack">
              <p>
                {pendingIntegrationConfirmation.nextStatus === "enabled"
                  ? "This lets HelloCashflow use this connection again for reporting and integration workflows."
                  : "This stops HelloCashflow from using this connection until an operator enables it again."}
              </p>
              <VNextKeyValueList
                items={[
                  {
                    label: "Integration",
                    value: pendingIntegrationConfirmation.displayName,
                  },
                  {
                    label: "Current status",
                    value: formatVNextLabel(
                      pendingIntegrationConfirmation.currentStatus,
                    ),
                  },
                  {
                    label: "New status",
                    value: formatVNextLabel(
                      pendingIntegrationConfirmation.nextStatus,
                    ),
                  },
                  {
                    label: "Category",
                    value: formatVNextLabel(
                      pendingIntegrationConfirmation.category,
                    ),
                  },
                  {
                    label: "Health",
                    value: formatVNextLabel(
                      pendingIntegrationConfirmation.healthStatus,
                    ),
                  },
                ]}
              />
            </div>
          </VNextDialog>
        ) : null}

        {feedback ? <VNextNotice>{feedback}</VNextNotice> : null}

        <VNextCard subtitle={workspaceHealthSubtitle} title="Workspace health">
          <div className="vnext-kpi-grid">
            {mode === "settings" ? (
              <VNextStat
                detail="Known integrations"
                label="Integrations"
                value={formatQueryCount(
                  listCount(integrationsQuery.data?.items),
                  integrationsQuery,
                )}
              />
            ) : (
              <>
                <VNextStat
                  detail="What needs attention rows"
                  label="Alerts"
                  value={formatQueryCount(
                    listCount(opsQuery.data?.items),
                    opsQuery,
                  )}
                />
                <VNextStat
                  detail="PostHog dashboards"
                  label="Dashboards"
                  value={formatQueryCount(
                    listCount(dashboardsQuery.data?.dashboards),
                    dashboardsQuery,
                  )}
                />
                <VNextStat
                  detail="Tracked taxonomy rows"
                  label="Events"
                  value={formatQueryCount(
                    listCount(taxonomyQuery.data?.items),
                    taxonomyQuery,
                  )}
                />
                <VNextStat
                  detail="Known integration connections"
                  label="Connections"
                  value={formatQueryCount(
                    listCount(integrationsQuery.data?.items),
                    integrationsQuery,
                  )}
                />
              </>
            )}
          </div>
        </VNextCard>

        {currentTab === "dashboard" ? (
          <VNextCard
            subtitle="Generated at the time the route was loaded."
            title="What needs attention"
          >
            {alerts.length === 0 ? (
              <div className="vnext-empty">
                No current insight alerts were returned.
              </div>
            ) : (
              <div className="vnext-grid">
                {alerts.map((item) => (
                  <div className="vnext-list-card" key={item.key}>
                    <div className="vnext-chip-list">
                      <VNextPill tone="danger">
                        {formatVNextLabel(item.severity)}
                      </VNextPill>
                      <VNextPill>{formatVNextLabel(item.category)}</VNextPill>
                    </div>
                    <div className="vnext-list-card__title">{item.title}</div>
                    <div>{item.description}</div>
                    {item.actionPath &&
                    (item.category !== "integrations" ||
                      canViewIntegrations) ? (
                      <Link
                        className="vnext-button vnext-button--quiet"
                        to={
                          item.category === "integrations"
                            ? "/settings/integrations"
                            : item.actionPath
                        }
                      >
                        {item.actionLabel ?? "Review in workspace"}
                      </Link>
                    ) : (
                      <div className="vnext-list-card__meta">
                        {item.actionLabel ?? "Review in workspace"}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </VNextCard>
        ) : null}

        {currentTab === "dashboards" ? (
          <VNextCard title="PostHog dashboards">
            {dashboardsQuery.isFetching ? (
              <div className="vnext-empty">Loading PostHog dashboards...</div>
            ) : dashboardsErrorDetails ? (
              <VNextNotice
                tone={
                  dashboardsQueryErrorCode === "posthog_not_configured"
                    ? "warning"
                    : "danger"
                }
              >
                <div>
                  {dashboardsQueryErrorCode === "posthog_not_configured"
                    ? "PostHog configuration is missing or invalid. Dashboards and usage metrics are unavailable until it is fixed."
                    : dashboardsQueryUsesCanonicalPublicError
                      ? dashboardsErrorDetails.message
                      : "Unable to load PostHog dashboards right now."}
                </div>
                {dashboardsQueryErrorCode === "posthog_not_configured" &&
                canViewIntegrations ? (
                  <Link
                    className="vnext-button vnext-button--quiet"
                    to="/settings/integrations"
                  >
                    Open integration settings
                  </Link>
                ) : null}
                <div className="vnext-list-card__meta">
                  Code:{" "}
                  {dashboardsQueryErrorCode ?? dashboardsErrorDetails.errorCode}
                  {dashboardsErrorDetails.correlationId
                    ? ` | Correlation: ${dashboardsErrorDetails.correlationId}`
                    : ""}
                </div>
              </VNextNotice>
            ) : dashboards.length === 0 ? (
              <div className="vnext-empty">
                No PostHog dashboards were returned for this workspace.
              </div>
            ) : (
              <div className="vnext-grid">
                {dashboards.map((dashboard) => (
                  <div className="vnext-list-card" key={dashboard.id}>
                    <div className="vnext-list-card__title">
                      {dashboard.name}
                    </div>
                    <div className="vnext-list-card__meta">
                      {dashboard.description ?? "No description"}
                    </div>
                    <VNextKeyValueList
                      items={[
                        {
                          label: "Updated",
                          value: formatVNextDateTime(dashboard.updatedAt),
                        },
                        {
                          label: "Visibility",
                          value: dashboard.dashboardUrl
                            ? "Link available"
                            : "Internal only",
                        },
                      ]}
                    />
                  </div>
                ))}
              </div>
            )}
          </VNextCard>
        ) : null}

        {currentTab === "taxonomy" ? (
          <div className="vnext-grid vnext-grid--two">
            <VNextCard title="Event taxonomy">
              {taxonomyItems.length === 0 ? (
                <div className="vnext-empty">
                  No taxonomy rows were returned.
                </div>
              ) : (
                <div className="vnext-scroll-panel">
                  <table className="vnext-table">
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th>Status</th>
                        <th>Annotation</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taxonomyItems.map((item) => (
                        <tr key={item.eventName}>
                          <td>{item.eventName}</td>
                          <td>{formatVNextLabel(item.status)}</td>
                          <td>{item.annotation ?? "n/a"}</td>
                          <td>
                            <VNextButton
                              aria-label={`Load ${item.eventName} into the taxonomy editor`}
                              className="vnext-table__action"
                              onClick={() => selectTaxonomyItem(item)}
                              tone="quiet"
                            >
                              Load
                            </VNextButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </VNextCard>

            <VNextCard title="Patch taxonomy event">
              <div className="vnext-grid">
                <VNextField label="Event name">
                  <VNextInput
                    onChange={(event) =>
                      setTaxonomyEventName(event.target.value)
                    }
                    value={taxonomyEventName}
                  />
                </VNextField>
                <VNextField label="Status">
                  <VNextSelect
                    onChange={(event) => setTaxonomyStatus(event.target.value)}
                    value={taxonomyStatus}
                  >
                    <option value="active">Active</option>
                    <option value="deprecated">Deprecated</option>
                  </VNextSelect>
                </VNextField>
                <VNextField label="Annotation">
                  <VNextTextArea
                    onChange={(event) =>
                      setTaxonomyAnnotation(event.target.value)
                    }
                    value={taxonomyAnnotation}
                  />
                </VNextField>
                <VNextField label="Deprecated reason">
                  <VNextTextArea
                    onChange={(event) =>
                      setTaxonomyDeprecatedReason(event.target.value)
                    }
                    value={taxonomyDeprecatedReason}
                  />
                </VNextField>
                <VNextField label="Replacement event name">
                  <VNextInput
                    onChange={(event) =>
                      setTaxonomyReplacement(event.target.value)
                    }
                    value={taxonomyReplacement}
                  />
                </VNextField>
                <div className="vnext-inline-actions">
                  <VNextButton
                    disabled={patchTaxonomyState.isLoading}
                    onClick={() => void handlePatchTaxonomy()}
                    tone="primary"
                  >
                    Save taxonomy patch
                  </VNextButton>
                </div>
              </div>
            </VNextCard>
          </div>
        ) : null}

        {currentTab === "integrations" ? (
          <VNextCard title="Integration connections">
            {integrationItems.length === 0 ? (
              <div className="vnext-empty">No integrations were returned.</div>
            ) : (
              <div className="vnext-grid">
                {integrationItems.map((integration) => (
                  <div
                    className="vnext-role-row"
                    key={integration.integrationKey}
                  >
                    <div>
                      <div className="vnext-role-row__name">
                        {integration.displayName}
                      </div>
                      <div className="vnext-role-row__meta">
                        {formatVNextLabel(integration.category)}
                      </div>
                      <div className="vnext-role-row__meta">
                        Connection:{" "}
                        {integration.connection
                          ? formatVNextLabel(integration.connection.status)
                          : "Not configured"}
                      </div>
                      <div className="vnext-role-row__meta">
                        Health:{" "}
                        {integration.health.reasonCodes.includes(
                          "missing_configuration",
                        )
                          ? "Missing or invalid configuration"
                          : formatVNextLabel(integration.health.status)}
                      </div>
                    </div>
                    <div className="vnext-inline-actions">
                      <VNextButton
                        aria-label={`${
                          integration.connection?.status === "enabled"
                            ? "Disable"
                            : "Enable"
                        } integration for ${integration.displayName}`}
                        disabled={
                          !canManageIntegrations ||
                          !integration.connection ||
                          patchConnectionState.isLoading
                        }
                        onClick={() =>
                          integration.connection
                            ? requestIntegrationStatusChange({
                                category: integration.category,
                                connectionId: integration.connection.id,
                                currentStatus: integration.connection.status,
                                displayName: integration.displayName,
                                healthStatus: integration.health.status,
                                nextStatus:
                                  integration.connection.status === "enabled"
                                    ? "disabled"
                                    : "enabled",
                              })
                            : undefined
                        }
                        tone="secondary"
                      >
                        {integration.connection?.status === "enabled"
                          ? `Disable ${integration.displayName}`
                          : `Enable ${integration.displayName}`}
                      </VNextButton>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </VNextCard>
        ) : null}
      </div>
    </WorkspaceChrome>
  );
}
